import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Languages,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Search,
  Upload,
  RefreshCw,
  X
} from 'lucide-react';
import { useAuth, useToast } from '@/shared/contexts';
import {
  localizationApi,
  type LocalizationKeyItem
} from '@/pages/SuperAdmin/api/localizationApi';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';

function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const pick = (value: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value
        .map(v => (typeof v === 'string' ? v : JSON.stringify(v)))
        .join(', ');
    }
    if (typeof value === 'object' && value !== null && 'message' in value) {
      return pick((value as { message: unknown }).message);
    }
    return null;
  };
  return pick(data) || fallback;
}

type EditorMode = 'create' | 'edit' | null;

const fieldClass =
  'flex h-10 w-full rounded-md border border-border-primary bg-background-primary px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006B82]/30 disabled:cursor-not-allowed disabled:opacity-50';

export const SuperAdminLocalizationsPage: React.FC = () => {
  const { accessToken } = useAuth();
  const { notify } = useToast();

  const [items, setItems] = useState<LocalizationKeyItem[]>([]);
  const [version, setVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [editKey, setEditKey] = useState('');
  const [editEn, setEditEn] = useState('');
  const [editEs, setEditEs] = useState('');
  const enInputRef = useRef<HTMLInputElement>(null);

  const [replaceLang, setReplaceLang] = useState<'en' | 'es'>('en');
  const [replacing, setReplacing] = useState(false);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await localizationApi.listKeys(accessToken);
      setItems(res.data.items || []);
      setVersion(res.data.version ?? null);
    } catch (err: unknown) {
      setError(extractApiError(err, 'Failed to load translations'));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!editorMode) return;
    const t = window.setTimeout(() => {
      enInputRef.current?.focus();
      enInputRef.current?.select();
    }, 50);
    return () => window.clearTimeout(t);
  }, [editorMode, editKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(row => {
      const en = row.translations.en || '';
      const es = row.translations.es || '';
      return (
        row.key.toLowerCase().includes(q) ||
        en.toLowerCase().includes(q) ||
        es.toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  const openCreate = () => {
    setError(null);
    setEditorMode('create');
    setEditKey('');
    setEditEn('');
    setEditEs('');
  };

  const openEdit = (row: LocalizationKeyItem) => {
    setError(null);
    setEditorMode('edit');
    setEditKey(row.key);
    setEditEn(row.translations.en || '');
    setEditEs(row.translations.es || '');
  };

  const closeEditor = () => {
    setEditorMode(null);
    setEditKey('');
    setEditEn('');
    setEditEs('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken || !editorMode) return;

    const key = editKey.trim();
    if (!key) {
      setError('Key is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (editorMode === 'create') {
        await localizationApi.createKey(accessToken, {
          key,
          en: editEn.trim() || key,
          es: editEs.trim() || undefined
        });
        notify('success', 'Translation key added');
      } else {
        await localizationApi.updateKey(accessToken, key, {
          en: editEn.trim() || key,
          es: editEs.trim() || undefined
        });
        notify('success', 'Translation updated');
      }
      closeEditor();
      await load();
    } catch (err: unknown) {
      setError(extractApiError(err, 'Failed to save translation'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!accessToken) return;
    const ok = window.confirm(
      `Remove "${key}" from the latest version?\nOlder versions will still keep this key.`
    );
    if (!ok) return;

    setSaving(true);
    setError(null);
    try {
      await localizationApi.removeKey(accessToken, key);
      notify('success', 'Key removed from latest version');
      await load();
    } catch (err: unknown) {
      setError(extractApiError(err, 'Failed to remove key'));
    } finally {
      setSaving(false);
    }
  };

  const handleReplaceFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !accessToken) return;

    setReplacing(true);
    setError(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('JSON must be an object of key → string');
      }
      const strings: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'string' && k.trim()) {
          strings[k] = v;
        }
      }
      if (Object.keys(strings).length === 0) {
        throw new Error('No valid translation strings found in JSON');
      }

      const res = await localizationApi.replaceLanguage(accessToken, {
        language: replaceLang,
        strings
      });
      notify(
        'success',
        `Replaced ${replaceLang.toUpperCase()} → version ${res.data.version} (${res.data.key_count} keys)`
      );
      await load();
    } catch (err: unknown) {
      setError(
        extractApiError(
          err,
          err instanceof Error ? err.message : 'Failed to replace language JSON'
        )
      );
    } finally {
      setReplacing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-[#006B82]" />
            <h2 className="text-xl font-semibold text-text-primary">
              Localizations
            </h2>
          </div>
          <p className="mt-1 text-sm text-text-tertiary">
            Manage English / Spanish UI strings. Changes publish a new immutable
            version for mobile apps.
          </p>
          <p className="mt-2 text-xs text-text-muted">
            Latest version:{' '}
            <span className="font-semibold text-text-primary">
              {version ?? '—'}
            </span>
            {' · '}
            {items.length} keys
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void load()}
            disabled={loading || saving}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button type="button" onClick={openCreate} disabled={loading || saving}>
            <Plus className="mr-2 h-4 w-4" />
            Add key
          </Button>
        </div>
      </div>

      {error && !editorMode && <Alert variant="destructive">{error}</Alert>}

      <div className="rounded-2xl border border-border-primary bg-background-card p-4 shadow-sm">
        <p className="text-sm font-medium text-text-primary">
          Replace language JSON
        </p>
        <p className="mt-1 text-xs text-text-tertiary">
          Uploads create a new version with ONLY keys from the file. Older
          versions stay saved.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <select
            value={replaceLang}
            onChange={e => setReplaceLang(e.target.value as 'en' | 'es')}
            className="rounded-lg border border-border-primary bg-background-primary px-3 py-2 text-sm text-text-primary"
          >
            <option value="en">English (en)</option>
            <option value="es">Spanish (es)</option>
          </select>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border-primary px-3 py-2 text-sm font-medium text-text-secondary hover:bg-background-hover">
            <Upload className="h-4 w-4" />
            {replacing ? 'Uploading…' : 'Upload JSON'}
            <input
              type="file"
              accept="application/json,.json"
              className="hidden"
              disabled={replacing || !accessToken}
              onChange={e => void handleReplaceFile(e)}
            />
          </label>
        </div>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search key / English / Spanish…"
          className={`${fieldClass} pl-9`}
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-border-primary bg-background-card shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-text-tertiary">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading translations…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            No translation keys found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-border-primary bg-background-secondary text-xs uppercase tracking-wide text-text-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Key</th>
                  <th className="px-4 py-3 font-medium">English</th>
                  <th className="px-4 py-3 font-medium">Spanish</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr
                    key={row.key}
                    className="border-b border-border-primary/70 last:border-0 hover:bg-background-hover/40"
                  >
                    <td className="max-w-[220px] truncate px-4 py-3 font-medium text-text-primary">
                      {row.key}
                    </td>
                    <td className="max-w-[260px] truncate px-4 py-3 text-text-secondary">
                      {row.translations.en || '—'}
                    </td>
                    <td className="max-w-[260px] truncate px-4 py-3 text-text-secondary">
                      {row.translations.es || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          className="rounded-md p-2 text-text-tertiary hover:bg-background-hover hover:text-[#006B82]"
                          title="Edit"
                          onClick={() => openEdit(row)}
                          disabled={saving}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="rounded-md p-2 text-text-tertiary hover:bg-state-error-bg hover:text-accent-error"
                          title="Remove from latest"
                          onClick={() => void handleDelete(row.key)}
                          disabled={saving}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editorMode && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close editor"
            className="absolute inset-0 bg-black/45"
            onClick={closeEditor}
          />
          <form
            onSubmit={e => void handleSave(e)}
            className="relative z-[81] w-full max-w-lg rounded-2xl border border-border-primary bg-background-card p-5 shadow-xl space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold text-text-primary">
                {editorMode === 'create'
                  ? 'Add translation key'
                  : 'Edit translation'}
              </h3>
              <button
                type="button"
                className="rounded-md p-1.5 text-text-tertiary hover:bg-background-hover"
                onClick={closeEditor}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {error && <Alert variant="destructive">{error}</Alert>}

            <div>
              <label className="mb-1 block text-xs font-medium text-text-tertiary">
                Key
              </label>
              <input
                value={editKey}
                onChange={e => setEditKey(e.target.value)}
                disabled={editorMode === 'edit' || saving}
                placeholder="e.g. Save Changes"
                required
                className={fieldClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-text-tertiary">
                English (en)
              </label>
              <input
                ref={enInputRef}
                value={editEn}
                onChange={e => setEditEn(e.target.value)}
                disabled={saving}
                placeholder="English text"
                className={fieldClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-text-tertiary">
                Spanish (es)
              </label>
              <input
                value={editEs}
                onChange={e => setEditEs(e.target.value)}
                disabled={saving}
                placeholder="Texto en español"
                className={fieldClass}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save (new version)'
                )}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={closeEditor}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
